import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { components } from "@/api/schema";

import type { MarketplaceId } from "../lib/types";

export type ListingDiscoverySummary = components["schemas"]["ListingDiscoverySummary"];
export type MarketplaceListing = components["schemas"]["MarketplaceListing"];
export type ListingStatus = MarketplaceListing["status"];

export const listingsKeys = {
  summary: (mkp: MarketplaceId) => ["marketplace-listings", "summary", mkp] as const,
  list: (mkp: MarketplaceId, status?: ListingStatus) =>
    ["marketplace-listings", "list", mkp, status ?? "all"] as const,
};

/**
 * GET /v1/marketplace/{mkp}/listings/summary — the shop's last discovery
 * result. A shop that has never run discovery 404s; that maps to `null`
 * ("never refreshed") rather than surfacing as a query error. `null`, not
 * `undefined`: TanStack Query v5 throws "data is undefined" for the latter.
 */
export function useListingsSummary(mkp: MarketplaceId, enabled = true) {
  return useQuery({
    queryKey: listingsKeys.summary(mkp),
    queryFn: async (): Promise<ListingDiscoverySummary | null> => {
      try {
        const { data, error } = await apiClient.GET(
          "/v1/marketplace/{marketplace}/listings/summary",
          { params: { path: { marketplace: mkp } } },
        );
        if (error) throw error;
        return data;
      } catch (err) {
        // Never-refreshed shop: BE 404s. That's a valid, expected state here
        // ("never refreshed"), not a query error.
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled,
  });
}

/** GET /v1/marketplace/{mkp}/listings — optionally filtered by mapping status. */
export function useListings(mkp: MarketplaceId, status?: ListingStatus, enabled = true) {
  return useQuery({
    queryKey: listingsKeys.list(mkp, status),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/marketplace/{marketplace}/listings", {
        params: { path: { marketplace: mkp }, query: status ? { status } : {} },
      });
      if (error) throw error;
      return data;
    },
    enabled,
  });
}

/**
 * POST /v1/marketplace/{mkp}/listings/refresh — pulls the shop's catalogue and
 * reconciles listing identities. Invalidates both the summary and the listing
 * list so the card and any open drawer pick up the new counts/rows.
 */
export function useRefreshListings(mkp: MarketplaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ListingDiscoverySummary> => {
      const { data, error } = await apiClient.POST(
        "/v1/marketplace/{marketplace}/listings/refresh",
        { params: { path: { marketplace: mkp } } },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["marketplace-listings", "summary", mkp] });
      void qc.invalidateQueries({ queryKey: ["marketplace-listings", "list", mkp] });
    },
  });
}
